import { FC, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    Box
} from '@material-ui/core';
import LoadingButton from '@material-ui/lab/LoadingButton';
import { InputField } from '../input-field';
import { companyCreate, CreateCompanyRequest } from '../../api/company';

interface CompanySetupDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const initialValues: CreateCompanyRequest = {
    name: '',
    description: '',
    website: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    tax_id: ''
};

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Company name is required').min(2, 'Company name must be at least 2 characters').max(100, 'Company name must be less than 100 characters'),
    description: Yup.string().max(500, 'Description must be less than 500 characters'),
    website: Yup.string().url('Must be a valid URL'),
    phone: Yup.string().max(20, 'Phone number must be less than 20 characters'),
    address_line1: Yup.string().max(100, 'Address must be less than 100 characters'),
    address_line2: Yup.string().max(100, 'Address must be less than 100 characters'),
    city: Yup.string().max(50, 'City must be less than 50 characters'),
    state: Yup.string().max(50, 'State must be less than 50 characters'),
    postal_code: Yup.string().max(20, 'Postal code must be less than 20 characters'),
    country: Yup.string().max(50, 'Country must be less than 50 characters'),
    tax_id: Yup.string().max(50, 'Tax ID must be less than 50 characters')
});

export const CompanySetupDialog: FC<CompanySetupDialogProps> = ({ open, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, helpers) => {
            try {
                setLoading(true);
                await companyCreate(values);
                toast.success('Company created successfully');
                onSuccess();
                onClose();
                helpers.setSubmitting(false);
            } catch (err) {
                console.error(err);
                toast.error('Failed to create company. Please try again.');
                helpers.setSubmitting(false);
            } finally {
                setLoading(false);
            }
        }
    });

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Typography variant="h6">
                    Set Up Your Company
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Please provide your company information to get started. This will be used for billing and subscription management.
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: 'white' }}>
                <Box sx={{ my: 2 }}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <InputField
                                    autoFocus
                                    error={Boolean(formik.touched.name && formik.errors.name)}
                                    fullWidth
                                    helperText={formik.touched.name && formik.errors.name}
                                    label="Company Name *"
                                    name="name"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.name}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputField
                                    error={Boolean(formik.touched.description && formik.errors.description)}
                                    fullWidth
                                    helperText={formik.touched.description && formik.errors.description}
                                    label="Description"
                                    name="description"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    multiline
                                    rows={3}
                                    value={formik.values.description}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputField
                                    error={Boolean(formik.touched.website && formik.errors.website)}
                                    fullWidth
                                    helperText={formik.touched.website && formik.errors.website}
                                    label="Website"
                                    name="website"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.website}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputField
                                    error={Boolean(formik.touched.phone && formik.errors.phone)}
                                    fullWidth
                                    helperText={formik.touched.phone && formik.errors.phone}
                                    label="Phone"
                                    name="phone"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.phone}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputField
                                    error={Boolean(formik.touched.address_line1 && formik.errors.address_line1)}
                                    fullWidth
                                    helperText={formik.touched.address_line1 && formik.errors.address_line1}
                                    label="Address Line 1"
                                    name="address_line1"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.address_line1}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputField
                                    error={Boolean(formik.touched.address_line2 && formik.errors.address_line2)}
                                    fullWidth
                                    helperText={formik.touched.address_line2 && formik.errors.address_line2}
                                    label="Address Line 2"
                                    name="address_line2"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.address_line2}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputField
                                    error={Boolean(formik.touched.city && formik.errors.city)}
                                    fullWidth
                                    helperText={formik.touched.city && formik.errors.city}
                                    label="City"
                                    name="city"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.city}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputField
                                    error={Boolean(formik.touched.state && formik.errors.state)}
                                    fullWidth
                                    helperText={formik.touched.state && formik.errors.state}
                                    label="State/Province"
                                    name="state"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.state}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputField
                                    error={Boolean(formik.touched.postal_code && formik.errors.postal_code)}
                                    fullWidth
                                    helperText={formik.touched.postal_code && formik.errors.postal_code}
                                    label="Postal Code"
                                    name="postal_code"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.postal_code}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputField
                                    error={Boolean(formik.touched.country && formik.errors.country)}
                                    fullWidth
                                    helperText={formik.touched.country && formik.errors.country}
                                    label="Country"
                                    name="country"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.country}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputField
                                    error={Boolean(formik.touched.tax_id && formik.errors.tax_id)}
                                    fullWidth
                                    helperText={formik.touched.tax_id && formik.errors.tax_id}
                                    label="Tax ID"
                                    name="tax_id"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.tax_id}
                                />
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <LoadingButton
                    color="primary"
                    onClick={() => formik.handleSubmit()}
                    loading={loading}
                    variant="contained"
                >
                    Create Company
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}; 